import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import type {
  IssueMcpTokenInput,
  IssueMcpTokenResult,
  ListMcpTokensResult,
  McpTokenSummary,
  RevokeMcpTokenInput,
  RevokeMcpTokenResult,
  UpdateMcpTokenExpiryInput,
  UpdateMcpTokenExpiryResult,
} from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * MCP アクセストークン管理 callable hooks。
 *
 * functions/src/mcp/manageMcpTokens.ts の issueMcpToken / revokeMcpToken / updateMcpTokenExpiry /
 * listMcpTokens を呼ぶ。
 * 一覧は手動 refresh 方式 (callable は subscribe できないため)。`useAdminUsers` の構造を踏襲。
 *
 * 認可は callable 側で強制 (master = 任意地区 / district admin = 自地区のみ)。UI でも地区選択を絞る。
 */

export type McpTokensListState =
  | { status: 'idle'; tokens: []; error: null }
  | { status: 'loading'; tokens: McpTokenSummary[]; error: null }
  | { status: 'ready'; tokens: McpTokenSummary[]; error: null }
  | { status: 'error'; tokens: []; error: Error };

export interface UseMcpTokensReturn {
  state: McpTokensListState;
  /** 一覧を (再) 取得。mutation 後に呼んで refresh する。 */
  refresh: () => Promise<void>;
  issueToken: (input: IssueMcpTokenInput) => Promise<IssueMcpTokenResult>;
  revokeToken: (input: RevokeMcpTokenInput) => Promise<RevokeMcpTokenResult>;
  /** 発行済みトークンの期限を「今日から N 日」に入れ直す。 */
  updateExpiry: (input: UpdateMcpTokenExpiryInput) => Promise<UpdateMcpTokenExpiryResult>;
}

export function useMcpTokens(): UseMcpTokensReturn {
  const [state, setState] = useState<McpTokensListState>({
    status: 'idle',
    tokens: [],
    error: null,
  });

  const refresh = useCallback(async () => {
    setState((prev) => ({
      status: 'loading',
      tokens: prev.status === 'ready' || prev.status === 'loading' ? prev.tokens : [],
      error: null,
    }));
    try {
      const fn = httpsCallable<unknown, ListMcpTokensResult>(functions, 'listMcpTokens');
      const res = await fn({});
      setState({ status: 'ready', tokens: res.data.tokens ?? [], error: null });
    } catch (e) {
      setState({ status: 'error', tokens: [], error: e as Error });
    }
  }, []);

  const issueToken = useCallback(
    async (input: IssueMcpTokenInput): Promise<IssueMcpTokenResult> => {
      const fn = httpsCallable<IssueMcpTokenInput, IssueMcpTokenResult>(functions, 'issueMcpToken');
      const res = await fn(input);
      return res.data;
    },
    [],
  );

  const revokeToken = useCallback(
    async (input: RevokeMcpTokenInput): Promise<RevokeMcpTokenResult> => {
      const fn = httpsCallable<RevokeMcpTokenInput, RevokeMcpTokenResult>(
        functions,
        'revokeMcpToken',
      );
      const res = await fn(input);
      return res.data;
    },
    [],
  );

  const updateExpiry = useCallback(
    async (input: UpdateMcpTokenExpiryInput): Promise<UpdateMcpTokenExpiryResult> => {
      const fn = httpsCallable<UpdateMcpTokenExpiryInput, UpdateMcpTokenExpiryResult>(
        functions,
        'updateMcpTokenExpiry',
      );
      const res = await fn(input);
      return res.data;
    },
    [],
  );

  return { state, refresh, issueToken, revokeToken, updateExpiry };
}
